import React, { useState } from 'react';
import _ from 'lodash';
import { Box, Divider, Typography } from '@mui/material';
import { bioFormatter } from './common';
import Hobbies from './Hobbies';
import Upvotes from './Upvotes';
import RoleTypeTag from './RoleTypeTag';
import YourLanguage from './YourLanguage';
import SocialHandles from './SocialHandles';
import WorkAndEduDesign from './WorkAndEduDesign';
import UserDigitalIntro from './UserDigitalIntro';
import MySkeleton from '../../../components/Skeleton';
import TextAndChipSection from './TextAndChipSection';
import TextAndLineSection from './TextAndLineSection';
import CallBookingModal from '../tabs/CallBookingModal';
import ChatAvatarComponent from '../tabs/chat/ChatAvatarComponent';
import CommonButton from '../../../components/ui/Buttons/CommonButton';
import { RequestMatch } from '../tabs/Matches/components/RequestAndRejectMatch';
import { dummySocialData, dummyWorkAndEduData } from '../../../utils/dummyLoadingData';
import { sortedCountries } from './common';

interface IUserProfileDetails {
  data?: any;
  loading?: boolean;
}

const UserProfileDetails: React.FC<IUserProfileDetails> = ({ data, loading }) => {
  const RequestMatchBtn = true;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openRequestMatch, setOpenRequestMatch] = useState(false);
  const workAndEdu = dummyWorkAndEduData;

  return (
    <>
      <Box>
        {/* Profile */}
        <Box display={'flex'} gap={2}>
          <ChatAvatarComponent
            height="96px"
            width="96px"
            fontSize={'30px'}
            image={data?.headshot}
            firstLetter={(data?.displayName).slice(0, 1)}
            loading={loading}
            type="noStatus"
          />
          <Box flex={1}>
            <Box display={'flex'} gap={1} alignItems="center">
              <Typography variant="h1">
                {loading ? <MySkeleton variant="text" height={20} width={100} /> : data?.displayName}
              </Typography>
              <Box>
                <Upvotes number={data?.userDetails?.upVote} loading={loading} />
              </Box>
            </Box>
            <Box my={0.5}>
              <Typography>
                {loading ? (
                  <MySkeleton variant="text" width={200} />
                ) : data?.userDetails?.personal_details?.category === 'Student' ? (
                  bioFormatter(
                    data?.userDetails?.professional_details?.education?.[0]?.major,
                    data?.userDetails?.professional_details?.education?.[0]?.university
                  )
                ) : (
                  bioFormatter(
                    data?.userDetails?.professional_details?.workHistory?.[0]?.company_name,
                    data?.userDetails?.professional_details?.workHistory?.[0]?.roleType?.[0]
                  )
                )}
              </Typography>
            </Box>
            <RoleTypeTag category={data?.userDetails?.personal_details?.category} loading={loading} />
          </Box>
        </Box>

        {/* Buttons */}
        {/* <Box gap={{ xs: 1, md: 2 }} py={2} display={'flex'} flexWrap={'wrap'} justifyContent={'end'}>
          {loading ? (
            Array.from({ length: RequestMatchBtn ? 3 : 2 }, (_, idx) => (
              <MySkeleton variant="rounded" height={35} width={140} sx={{ borderRadius: '20px' }} />
            ))
          ) : (
            <>
              <CommonButton title='Message' variant="outlined" />
              <CommonButton title='Meeting' variant="outlined" onClick={() => setIsCalendarOpen(true)} />
              {RequestMatchBtn && (
                <CommonButton title='Request a Match' onClick={() => setOpenRequestMatch(true)} />
              )}
            </>
          )}
        </Box> */}
        <Divider />

        {/* Image / Video */}
        <UserDigitalIntro data={data} loading={loading} />

        {/* Bio */}
        <TextAndLineSection heading="Bio" data={data?.userDetails?.personal_details?.bio} />
        <Divider />
        
        {/* Country */}
        <TextAndLineSection
          heading="Country"
          data={
            sortedCountries.find((country) => country.code === data?.userDetails?.personal_details?.country)?.label ||
            'Not yet added'
          }
        />

        {/* Topics & Industries */}
        <TextAndChipSection
          chipType="outline"
          param="IcanHelpYouWith"
          heading="Topics & Industries"
          data={[...(data?.userDetails?.savedTopics || []), ...(data?.userDetails?.savedIndustries || [])]}
        />
        <Divider />

        {/* Work Experience */}
        <Box py={2}>
          <Box>
            <Typography variant="h5">
              {loading ? <MySkeleton variant="text" height={16} width={150} /> : 'Work Experience'}
            </Typography>
          </Box>
          <Box pt={2}>
            {loading ? (
              <MySkeleton variant="text" height={16} width={'100%'} />
            ) : data?.userDetails?.professional_details?.workHistory?.length > 0 ? (
              <WorkAndEduDesign
                data={data?.userDetails?.professional_details.workHistory}
                itemType="work"
                wherePrint="communityUserProfile"
                onEdit={(item) => {}}
              />
            ) : (
              <Typography>Not added yet</Typography>
            )}
          </Box>
        </Box>
        <Divider />

        {/* Education and Skills */}
        <Box py={2}>
          <Box>
            <Typography variant="h5">
              {loading ? <MySkeleton variant="text" height={16} width={150} /> : 'Education and Skills'}
            </Typography>
          </Box>
          <Box pt={2}>
            {loading ? (
              <MySkeleton variant="text" height={16} width={'100%'} />
            ) : data?.userDetails?.professional_details?.education?.length > 0 ? (
              <WorkAndEduDesign
                data={data?.userDetails?.professional_details.education}
                itemType="education"
                wherePrint="communityUserProfile"
                onEdit={(item) => {}}
              />
            ) : (
              <Typography>No Education</Typography>
            )}
          </Box>
        </Box>
        <Divider />

        {/* Hobbies and interests */}
        <Hobbies hobbiesValue={data.userDetails?.personal_details?.hobbies} />
        <Divider />

        {/* Language You Speak */}
        {/* <YourLanguage />
        <Divider /> */}

        {/* Social handles */}
        <SocialHandles socialData={data.userDetails?.personal_details?.social} />
        <Divider />

        {/* My Programs */}
        <TextAndChipSection heading="My Programs" data={data?.userDetails?.allGroupsUserBelongsTo} />
      </Box>

      {/* Call Booking Modal */}
      <CallBookingModal
        handleCloseModal={() => setIsCalendarOpen(false)}
        openFlag={isCalendarOpen}
        chatProfileName={''}
        threadParticipantsUsers={[]}
        chatType="Chats"
        pageName="Community"
        bookedCallDetails={''}
      />

      {/* Request a Match Modal */}
      <RequestMatch openRequestMatch={openRequestMatch} handleRequestMatch={() => setOpenRequestMatch(false)} />
    </>
  );
};

export default UserProfileDetails;
